import { Download, Edit, ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Typography
} from '@mui/material';
import { FC, useState } from 'react';
import { useQuery } from 'react-query';
import { Loader, Page, PageTitleWithBack } from '../../../../components';
import {
  getAddendumA,
  getNeighborhoodDetails,
  getNeighborhoodSeriesBudgetOptions,
  getNeighborhoodSeriesGeneralInfo,
  getNeighborhoodSeriesSelectedBudgetOptions
} from '../../../../fetch';
import { downloadFile, formatMoney } from '../../../../helpers';
import {
  IBudgetOption,
  INeighborhoodDetailDescription,
  ISelectedBudgetOption,
  ISeries
} from '../../../../models';
import { SeriesDetailModal } from './series-details-modal';
import { useHistory, useParams } from 'react-router-dom';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { SeriesBudgetOptionsModal } from './budget-options/series-budget-options';
import { useSnackbar } from 'notistack';

interface ISeriesDetailsPage {}

export const SeriesDetailsPage: FC<ISeriesDetailsPage> = () => {
  const { neighborhoodId, selectedSeriesId }: { neighborhoodId: string; selectedSeriesId: string } =
    useParams();
  const classes = useStyles();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBudgetOptionsModalOpen, setIsBudgetOptionsModalOpen] = useState(false);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const {
    isLoading: isLoadingSeriesGeneralInfoData,
    data: neighborhoodsGeneralInfo,
    refetch: refetchNeighborhoodGeneralInfo
  } = useQuery<ISeries, Error>(
    ['neighborhoodsGeneralInfo'],
    () => getNeighborhoodSeriesGeneralInfo(neighborhoodId, selectedSeriesId),
    {
      onSuccess: data => {},
      notifyOnChangeProps: 'tracked'
    }
  );

  const { isLoading: isLoadingNeighborhoodsBudgetOptions, data: neighborhoodsBudgetOptions } =
    useQuery<IBudgetOption[], Error>(
      ['neighborhoodsBudgetOptions'],
      () => getNeighborhoodSeriesBudgetOptions(),
      {
        onSuccess: data => {},
        notifyOnChangeProps: 'tracked'
      }
    );

  const { isLoading: isLoadingNeighborhoodDetails, data: neighborhoodDetails } = useQuery<
    INeighborhoodDetailDescription,
    Error
  >(['neighborhoodDetails'], () => getNeighborhoodDetails(neighborhoodId), {
    onSuccess: data => {},
    notifyOnChangeProps: 'tracked'
  });

  const [selectedOptions, setSelectedOptions] = useState<ISelectedBudgetOption[]>(null);
  const {
    isLoading: isLoadingSeriesSelectedBudgetOptionsData,
    data: neighborhoodsSelectedBudgetOptions,
    refetch: refetchNeighborhoodSelectedBudgetOptions,
    isRefetching: isRefetchingSeriesSelectedBudgetOptionsData
  } = useQuery<ISelectedBudgetOption[], Error>(
    ['neighborhoodsSelectedBudgetOptions'],
    () => getNeighborhoodSeriesSelectedBudgetOptions(neighborhoodId, selectedSeriesId),
    {
      onSuccess: data => {
        setSelectedOptions(data);
      },
      notifyOnChangeProps: 'tracked'
    }
  );

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleBudgetOptionsModalClose = (cancelled: boolean) => {
    setIsBudgetOptionsModalOpen(false);
    if (!cancelled) {
      refetchNeighborhoodSelectedBudgetOptions();
    }
  };

  const [expanded, setExpanded] = useState([]);
  const handleAccordionChange = panel => {
    if (expanded?.includes(panel)) {
      setExpanded(expanded?.filter(p => p !== panel));
    } else {
      setExpanded([...expanded, panel]);
    }
  };

  const handleEditStandardFeatures = () => {
    history.push(
      `/development/neighborhoods/${neighborhoodId}/options/${selectedSeriesId}/standard-features`
    );
  };
  const handleEditExcludedOptions = () => {
    history.push(
      `/development/neighborhoods/${neighborhoodId}/options/${selectedSeriesId}/excluded-options`
    );
  };
  const handleEditExcludedColors = () => {
    history.push(
      `/development/neighborhoods/${neighborhoodId}/options/${selectedSeriesId}/excluded-colors`
    );
  };
  return (
    <Page title={'Series Detail'}>
      <>
        <PageTitleWithBack
          title={
            !isLoadingSeriesGeneralInfoData && !isLoadingNeighborhoodDetails
              ? `Edit ${neighborhoodDetails ? `${neighborhoodDetails?.name} - ` : ''}${
                  neighborhoodsGeneralInfo?.name
                }`
              : 'Loading...'
          }
          backText="Back to neighborhoods"
          backUrl="/development/neighborhoods"
        />
        {(isLoadingSeriesGeneralInfoData ||
          isLoadingSeriesSelectedBudgetOptionsData ||
          isLoadingNeighborhoodsBudgetOptions ||
          isLoadingNeighborhoodDetails) && <Loader type="overlay" position="centered" />}
        {(!isLoadingSeriesGeneralInfoData ||
          !isLoadingSeriesSelectedBudgetOptionsData ||
          !isLoadingNeighborhoodsBudgetOptions ||
          !isLoadingNeighborhoodDetails) && (
          <Box
            margin={{
              xs: '1rem 0 0 0',
              sm: '1rem 0 0 1rem'
            }}
          >
            <Accordion
              key={`series-general-info`}
              defaultExpanded={false}
              expanded={expanded?.includes(`series-general-info`)}
              onChange={() => handleAccordionChange(`series-general-info`)}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls={`series-general-info`}
                id={`series-general-info`}
              >
                <div className={classes.topPanelSummaryWrapper}>
                  <div>
                    <Grid container alignItems={'center'} wrap="nowrap">
                      <Typography
                        variant="h6"
                        className={classes.boldName}
                        component="span"
                        color="primary"
                      >
                        Series
                      </Typography>
                    </Grid>
                  </div>
                  <div className={classes.buttonsWrapper}>
                    <IconButton
                      color="primary"
                      aria-label="Edit Series"
                      size="large"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsModalOpen(true);
                      }}
                    >
                      <Edit />
                    </IconButton>
                  </div>
                </div>
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                <Grid container spacing={1}>
                  <Grid item xs={6} sm={2}>
                    <Typography color="primary">Name</Typography>
                    <Typography variant="subtitle1" component="div">
                      {neighborhoodsGeneralInfo?.name ? neighborhoodsGeneralInfo?.name : 'No Name'}
                    </Typography>
                    <Typography color="primary">Plan Styles</Typography>
                    <Typography variant="subtitle1" component="div">
                      {neighborhoodsGeneralInfo?.selectedPlanStyles &&
                      neighborhoodsGeneralInfo?.selectedPlanStyles.length > 0
                        ? neighborhoodsGeneralInfo?.selectedPlanStyles.join(', ')
                        : 'No Plan Styles'}
                    </Typography>
                    <Typography color="primary">Excluded Plans</Typography>
                    <Typography variant="subtitle1" component="div">
                      {neighborhoodsGeneralInfo?.excludedPlanCodes &&
                      neighborhoodsGeneralInfo?.excludedPlanCodes.length > 0
                        ? neighborhoodsGeneralInfo?.excludedPlanCodes.join(', ')
                        : 'No Excluded Plans'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Typography color="primary">Additional Option Cap</Typography>
                    <Typography variant="subtitle1" component="div">
                      {neighborhoodsGeneralInfo?.additionalUpgradeCap
                        ? formatMoney(neighborhoodsGeneralInfo?.additionalUpgradeCap)
                        : 'No Additional Option Cap'}
                    </Typography>
                    <Typography color="primary">Option Line</Typography>
                    <Typography variant="subtitle1" component="div">
                      {neighborhoodsGeneralInfo?.optionLine
                        ? neighborhoodsGeneralInfo?.optionLine.replace(/([A-Z])/g, ' $1').trim()
                        : 'No Option Line'}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
            <Accordion
              key={`series-standard-features`}
              defaultExpanded={false}
              expanded={expanded?.includes(`series-standard-features`)}
              onChange={() => handleAccordionChange(`series-standard-features`)}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls={`series-standard-features`}
                id={`series-standard-features`}
              >
                <div className={classes.topPanelSummaryWrapper}>
                  <div>
                    <Grid container alignItems={'center'} wrap="nowrap">
                      <Typography
                        variant="h6"
                        className={classes.boldName}
                        component="span"
                        color="primary"
                      >
                        Standard Features
                      </Typography>
                    </Grid>
                  </div>
                  <div className={classes.buttonsWrapper}>
                    <IconButton
                      color="primary"
                      aria-label="Edit Series"
                      size="large"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleEditStandardFeatures();
                      }}
                    >
                      <Edit />
                    </IconButton>
                  </div>
                </div>
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography>
                      Please click the edit button to see the selected Standard Features.
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
            <Accordion
              key={`series-budget-options`}
              defaultExpanded={false}
              expanded={expanded?.includes(`series-budget-options`)}
              onChange={() => handleAccordionChange(`series-budget-options`)}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls={`series-budget-options`}
                id={`series-budget-options`}
              >
                <div className={classes.topPanelSummaryWrapper}>
                  <div>
                    <Grid container alignItems={'center'} wrap="nowrap">
                      <Typography
                        variant="h6"
                        className={classes.boldName}
                        component="span"
                        color="primary"
                      >
                        Budget Options
                      </Typography>
                    </Grid>
                  </div>
                  <div className={classes.buttonsWrapper}>
                    <IconButton
                      color="primary"
                      aria-label="Edit Series"
                      size="large"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsBudgetOptionsModalOpen(true);
                      }}
                    >
                      <Edit />
                    </IconButton>
                  </div>
                </div>
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                <Grid container spacing={1}>
                  {isRefetchingSeriesSelectedBudgetOptionsData ? (
                    <Grid item xs={12}>
                      <Loader type="inline" position="centered" />
                    </Grid>
                  ) : neighborhoodsSelectedBudgetOptions?.length > 0 ? (
                    neighborhoodsBudgetOptions?.map(option => {
                      const selected = neighborhoodsSelectedBudgetOptions?.find(
                        budgetOption => option?.optionId === budgetOption?.optionId
                      );
                      return !isNaN(selected?.price) ? (
                        <Grid item xs={12}>
                          <Typography>
                            {`${option.optionSetName} - ${option.name} - ${formatMoney(
                              selected?.price
                            )}`}
                          </Typography>
                        </Grid>
                      ) : null;
                    })
                  ) : (
                    <Grid item xs={12}>
                      <Typography>No Budget Options added.</Typography>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
            <Accordion
              key={`series-excluded-options`}
              defaultExpanded={false}
              expanded={expanded?.includes(`series-excluded-options`)}
              onChange={() => handleAccordionChange(`series-excluded-options`)}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls={`series-excluded-options`}
                id={`series-excluded-options`}
              >
                <div className={classes.topPanelSummaryWrapper}>
                  <div>
                    <Grid container alignItems={'center'} wrap="nowrap">
                      <Typography
                        variant="h6"
                        className={classes.boldName}
                        component="span"
                        color="primary"
                      >
                        Excluded Options
                      </Typography>
                    </Grid>
                  </div>
                  <div className={classes.buttonsWrapper}>
                    <IconButton
                      color="primary"
                      aria-label="Edit Series"
                      size="large"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleEditExcludedOptions();
                      }}
                    >
                      <Edit />
                    </IconButton>
                  </div>
                </div>
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography>
                      Please click the edit button to see the Excluded Options.
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
            <Accordion
              key={`series-excluded-colors`}
              defaultExpanded={false}
              expanded={expanded?.includes(`series-excluded-colors`)}
              onChange={() => handleAccordionChange(`series-excluded-colors`)}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls={`series-excluded-colors`}
                id={`series-excluded-colors`}
              >
                <div className={classes.topPanelSummaryWrapper}>
                  <div>
                    <Grid container alignItems={'center'} wrap="nowrap">
                      <Typography
                        variant="h6"
                        className={classes.boldName}
                        component="span"
                        color="primary"
                      >
                        Excluded Colors
                      </Typography>
                    </Grid>
                  </div>
                  <div className={classes.buttonsWrapper}>
                    <IconButton
                      color="primary"
                      aria-label="Edit Series"
                      size="large"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleEditExcludedColors();
                      }}
                    >
                      <Edit />
                    </IconButton>
                  </div>
                </div>
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography>
                      Please click the edit button to see the Excluded Colors.
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
            <Box mt={1}>
              <Box mt={1} mb={0.5}>
                <Divider />
              </Box>
              <Box display={'flex'} justifyContent={'end'}>
                <Box>
                  <Button
                    startIcon={<Download />}
                    sx={{
                      width: {
                        xs: '100%',
                        sm: 'auto'
                      }
                    }}
                    onClick={async () => {
                      try {
                        const addendumA = await getAddendumA(neighborhoodId, selectedSeriesId);
                        const fileHeader = addendumA.headers['content-disposition'];
                        const fileName = fileHeader
                          .split(';')[1]
                          .split('filename=')[1]
                          .replaceAll('"', '');
                        downloadFile(addendumA.data, fileName);
                      } catch (error) {
                        enqueueSnackbar(`Error, unable to download file, please try again.`, {
                          variant: 'error'
                        });
                      }
                    }}
                  >
                    Addendum A
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        <SeriesDetailModal
          open={isModalOpen}
          modalIsLoading={isLoadingSeriesGeneralInfoData}
          onClose={() => handleModalClose()}
          currentSeries={neighborhoodsGeneralInfo}
          fetchSeries={() => refetchNeighborhoodGeneralInfo()}
          selectedSeriesId={selectedSeriesId}
          neighborhoodId={neighborhoodId}
        />
        <SeriesBudgetOptionsModal
          open={isBudgetOptionsModalOpen}
          modalIsLoading={isLoadingSeriesGeneralInfoData}
          onClose={cancelled => handleBudgetOptionsModalClose(cancelled)}
          budgetOptions={neighborhoodsBudgetOptions}
          selectedBudgetOptions={selectedOptions}
          originalSelectedBudgetOptions={neighborhoodsSelectedBudgetOptions}
          setSelectedBudgetOptions={setSelectedOptions}
          fetchBudgetOptions={() => refetchNeighborhoodSelectedBudgetOptions()}
          selectedSeriesId={selectedSeriesId}
          neighborhoodId={neighborhoodId}
        />
      </>
    </Page>
  );
};
const useStyles = makeStyles((theme: Theme) => ({
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  },
  topWrapper: {
    marginBottom: theme.spacing(0.5),
    justifyContent: 'center',
    flexDirection: 'column',
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'space-between',
      flexDirection: 'row'
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  buttonsWrapper: {
    display: 'flex',
    marginRight: theme.spacing(1)
  },
  boldName: {
    color: theme.palette.primary.main,
    fontWeight: 600
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  }
}));
